import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheck, FiStar, FiShield, FiUsers, FiAward, FiBookOpen } from 'react-icons/fi';
import { HiOutlineScale, HiOutlineBriefcase, HiOutlineUserGroup, HiOutlineHome, HiOutlineOfficeBuilding, HiOutlineDocumentText } from 'react-icons/hi';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

function HomeModern() {
  const practiceAreas = [
    { icon: <HiOutlineDocumentText className="w-7 h-7" />, title: 'IPC & Criminal Law', description: 'Expert defense under Indian Penal Code and CrPC. Representation in criminal cases under Indian jurisprudence', color: 'from-red-500 to-rose-600' },
    { icon: <HiOutlineBriefcase className="w-7 h-7" />, title: 'Civil & Contract Law', description: 'Civil disputes, contracts, and resolution under Indian Contract Act 1872', color: 'from-blue-500 to-indigo-600' },
    { icon: <HiOutlineUserGroup className="w-7 h-7" />, title: 'Family Law', description: 'Divorce, custody, inheritance under Hindu Marriage Act and Indian family laws', color: 'from-purple-500 to-violet-600' },
    { icon: <HiOutlineHome className="w-7 h-7" />, title: 'Property & Real Estate', description: 'Property disputes, transfer, and inheritance under Indian Transfer of Property Act', color: 'from-emerald-500 to-green-600' },
    { icon: <HiOutlineOfficeBuilding className="w-7 h-7" />, title: 'Corporate & Tax Law', description: 'Corporate governance, taxation, and compliance under Indian Income Tax Act', color: 'from-amber-500 to-orange-600' },
    { icon: <HiOutlineScale className="w-7 h-7" />, title: 'Constitutional Law', description: 'Rights under Indian Constitution, fundamental freedoms, and legal remedies', color: 'from-teal-500 to-cyan-600' },
  ];

  const stats = [
    { number: '10,000+', label: 'Clients Served', icon: <FiUsers className="w-5 h-5" /> },
    { number: '500+', label: 'Expert Lawyers', icon: <FiShield className="w-5 h-5" /> },
    { number: '98%', label: 'Success Rate', icon: <FiAward className="w-5 h-5" /> },
    { number: '50+', label: 'Practice Areas', icon: <FiBookOpen className="w-5 h-5" /> },
  ];

  const steps = [
    { num: '01', title: 'Book Consultation', desc: 'Schedule a free initial consultation with one of our experienced attorneys' },
    { num: '02', title: 'Case Evaluation', desc: 'We thoroughly evaluate your case and provide professional legal advice' },
    { num: '03', title: 'Legal Strategy', desc: 'We develop a comprehensive strategy tailored to your specific needs' },
    { num: '04', title: 'Resolution', desc: 'We work diligently to achieve the best possible outcome for your case' },
  ];

  const testimonials = [
    { name: 'Rajesh Kumar', title: 'Business Owner', text: 'Exceptional legal expertise. They handled my corporate matter with precision and professionalism as per Indian law. Highly recommended.', rating: 5 },
    { name: 'Priya Sharma', title: 'Individual Client', text: 'The team provided compassionate representation in my family matter. I felt heard and well-supported throughout the legal process.', rating: 5 },
    { name: 'Amit Patel', title: 'Corporate Executive', text: 'Outstanding legal strategy under Indian tax laws. Their attention to detail and proactive approach saved us significant time and resources.', rating: 5 },
  ];

  return (
    <div className="overflow-hidden">
      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 40%, #172e4d 100%)' }}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 70%)' }} />
          <div className="absolute bottom-20 left-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.3) 0%, transparent 70%)' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-sm text-white/80 mb-8">
                <FiShield className="w-4 h-4 text-accent-400" />
                Trusted by 10,000+ Clients Across India
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Expert Legal Solutions for
                <span className="block text-gradient-accent mt-2">Your Rights & Justice</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-lg text-white/60 max-w-xl mb-10 leading-relaxed">
                Professional legal services for individuals and businesses across India. Expert representation in all areas of Indian law and Constitution.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <Link to="/booking" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-primary-900 transition-all duration-300 hover:shadow-lg hover:scale-105" style={{ background: 'linear-gradient(135deg, #d4af37, #f0d060)' }}>
                  Book Consultation
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/lawyers" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white border-2 border-white/30 hover:bg-white/10 transition-all duration-300">
                  Find a Lawyer
                </Link>
              </motion.div>

              <motion.div variants={fadeUp} className="flex items-center gap-6 mt-12">
                {[
                  'Free Initial Consultation',
                  '100% Confidential',
                  'All Courts Covered'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white/50">
                    <FiCheck className="w-4 h-4 text-accent-400 flex-shrink-0" />
                    <span className="hidden sm:inline">{item}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right side - Stats card */}
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, i) => (
                    <div key={i} className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
                      <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center mx-auto mb-3 text-accent-400">
                        {stat.icon}
                      </div>
                      <p className="text-3xl font-bold text-white mb-1">{stat.number}</p>
                      <p className="text-sm text-white/50">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PRACTICE AREAS ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#d4af37' }}>Our Expertise</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">Practice Areas</motion.h2>
            <motion.p variants={fadeUp} className="text-secondary-500 max-w-2xl mx-auto">Comprehensive legal solutions across multiple disciplines of Indian law</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {practiceAreas.map((area, idx) => (
              <motion.div key={idx} variants={fadeUp}>
                <Link to="/lawyers" className="group block p-8 rounded-2xl border border-secondary-100 hover:border-secondary-200 bg-white hover:shadow-xl transition-all duration-300">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${area.color} flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    {area.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2 group-hover:text-primary-500 transition-colors">{area.title}</h3>
                  <p className="text-sm text-secondary-500 leading-relaxed mb-4">{area.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1">
                    Learn More <FiArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-24 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#d4af37' }}>Simple Process</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">How It Works</motion.h2>
            <motion.p variants={fadeUp} className="text-secondary-500 max-w-2xl mx-auto">Get the legal help you need in just four simple steps</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <motion.div key={idx} variants={fadeUp} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 text-center relative z-10">
                  <div className="text-5xl font-bold mb-4 text-gradient-accent">{step.num}</div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-secondary-500 leading-relaxed">{step.desc}</p>
                </div>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 text-secondary-300 z-20">
                    <FiArrowRight className="w-6 h-6" />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.p variants={fadeUp} className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#d4af37' }}>Why Choose Us</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">Excellence in Legal Representation</motion.h2>
              <motion.p variants={fadeUp} className="text-secondary-500 mb-10 leading-relaxed">
                We combine decades of legal expertise with a client-first approach to deliver exceptional results in every case.
              </motion.p>

              <motion.div variants={staggerContainer} className="space-y-6">
                {[
                  { icon: '🎯', title: 'Client-Focused Approach', desc: 'We prioritize your needs and work tirelessly to achieve the best outcomes' },
                  { icon: '📚', title: '25+ Years Experience', desc: 'Decades of combined legal expertise and proven track record of success' },
                  { icon: '🔒', title: 'Confidential Consultation', desc: 'Your privacy and confidentiality are paramount in all matters' },
                  { icon: '💡', title: 'Professional Guidance', desc: 'Expert legal advice tailored to your unique situation and objectives' },
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeUp} className="flex gap-5 p-5 rounded-xl hover:bg-secondary-50 transition-colors duration-200 group">
                    <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 text-xl group-hover:scale-110 transition-transform">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-secondary-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-secondary-500">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.1), rgba(212,175,55,0.1))' }} />
                <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-secondary-100">
                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat, i) => (
                      <div key={i} className="p-6 rounded-xl bg-secondary-50 text-center">
                        <p className="text-2xl font-bold text-primary-500">{stat.number}</p>
                        <p className="text-xs text-secondary-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-primary-500 to-primary-700 text-white text-center">
                    <p className="font-semibold">Trusted Legal Partner</p>
                    <p className="text-sm text-white/70 mt-1">Serving clients across all Indian courts</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#d4af37' }}>Testimonials</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">What Our Clients Say</motion.h2>
            <motion.p variants={fadeUp} className="text-secondary-500 max-w-2xl mx-auto">Hear from our satisfied clients about their experience</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <motion.div key={idx} variants={fadeUp} className="bg-white rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 border border-secondary-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <FiStar key={i} className="w-4 h-4 fill-accent-400 text-accent-400" />
                  ))}
                </div>
                <p className="text-secondary-600 leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-secondary-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center text-white font-semibold text-sm">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 text-sm">{t.name}</p>
                    <p className="text-xs text-secondary-400">{t.title}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f1f35 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Get Started?</motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Let our expert legal team help you navigate the complexities of law. Book a free consultation today.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/booking" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-primary-900 transition-all duration-300 hover:shadow-lg hover:scale-105" style={{ background: 'linear-gradient(135deg, #d4af37, #f0d060)' }}>
                Book Your Consultation
                <FiArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white border-2 border-white/30 hover:bg-white/10 transition-all duration-300">
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default HomeModern;
